// app/frontend/data/danceStyleDictionaryEntries.ts
export interface DictionaryEntry {
  id: number;
  name: string;
  definition: string;
  category: (
    | "Dance"
    | "History"
    | "Culture"
    | "Music"
    | "Person"
    | "Place"
    | "Umbrella"
  )[];
  danceStyles: number[];
  multimedia: number[];
  seeAlso: number[];
}

export const danceStyleDictionaryEntries: DictionaryEntry[] = [
  // EXISTING DANCE TECHNIQUE ENTRIES (1-35) - PRESERVED WITH UPDATED CROSS-REFERENCES
  {
    id: 1,
    name: "Saida",
    definition:
      "A fundamental movement in Kizomba where the couple moves forward and backward in a walking pattern, establishing the basic rhythm and connection.",
    category: ["Dance"],
    danceStyles: [1, 2, 3],
    multimedia: [1, 2, 3],
    seeAlso: [2, 3, 4, 5, 36], // Added connection to Kizomba umbrella
  },
  {
    id: 2,
    name: "Connection",
    definition:
      "The physical and emotional communication between dance partners through touch, weight sharing, and energy exchange.",
    category: ["Dance"],
    danceStyles: [1, 5, 9, 13],
    multimedia: [3, 4],
    seeAlso: [1, 8, 9, 15, 25, 24],
  },
  {
    id: 3,
    name: "Basic Step",
    definition:
      "The fundamental step pattern that forms the foundation of the dance, typically learned first by beginners.",
    category: ["Dance"],
    danceStyles: [2, 6, 10],
    multimedia: [1],
    seeAlso: [1, 2, 8, 11, 13],
  },
  {
    id: 4,
    name: "Entrada",
    definition:
      "An entry movement in Kizomba where the leader guides the follower into the dance hold or into a new pattern.",
    category: ["Dance"],
    danceStyles: [1, 2],
    multimedia: [1, 2],
    seeAlso: [1, 5, 36, 37],
  },
  {
    id: 5,
    name: "Caminhada",
    definition:
      "A walking sequence in Kizomba characterized by smooth, grounded steps that emphasize the connection with the floor.",
    category: ["Dance"],
    danceStyles: [1, 2, 3],
    multimedia: [1, 3],
    seeAlso: [1, 4, 6, 25, 36],
  },
  {
    id: 6,
    name: "Ginga",
    definition:
      "A characteristic swaying movement of the hips and torso, fundamental to many African and African-diaspora dances.",
    category: ["Dance"],
    danceStyles: [1, 3],
    multimedia: [1, 2, 4],
    seeAlso: [5, 7, 35, 38, 53],
  },
  {
    id: 7,
    name: "Isolations",
    definition:
      "Controlled movement of individual body parts while keeping other parts of the body still, crucial for body awareness.",
    category: ["Dance"],
    danceStyles: [1, 4, 13],
    multimedia: [1, 2],
    seeAlso: [6, 8, 25, 39, 23],
  },
  {
    id: 8,
    name: "Frame",
    definition:
      "The structural connection between partners created by arm positioning and torso engagement in partner dancing.",
    category: ["Dance"],
    danceStyles: [5, 6, 7, 8],
    multimedia: [2, 3],
    seeAlso: [2, 3, 9, 40, 41],
  },
  {
    id: 9,
    name: "Lead and Follow",
    definition:
      "The communication system in partner dancing where one person (lead) initiates movements and the other (follow) responds.",
    category: ["Dance"],
    danceStyles: [1, 5, 9],
    multimedia: [3, 4],
    seeAlso: [2, 8, 15, 40, 41],
  },
  {
    id: 10,
    name: "Swing Out",
    definition:
      "A signature move in Lindy Hop where the follower is sent out to open position and brought back to closed position.",
    category: ["Dance"],
    danceStyles: [6],
    multimedia: [1, 3],
    seeAlso: [11, 12, 42, 43],
  },
  {
    id: 11,
    name: "Rock Step",
    definition:
      "A fundamental movement in swing dances involving a weight transfer backward and forward on the balls of the feet.",
    category: ["Dance"],
    danceStyles: [6, 7],
    multimedia: [1],
    seeAlso: [3, 10, 12, 42],
  },
  {
    id: 12,
    name: "Charleston",
    definition:
      "A rhythmic step pattern characterized by twisting leg movements, often incorporated into Lindy Hop.",
    category: ["Dance"],
    danceStyles: [6],
    multimedia: [1, 3, 4],
    seeAlso: [10, 11, 32, 43, 59],
  },
  {
    id: 13,
    name: "Cross Body Lead",
    definition:
      "A fundamental turn pattern in Salsa where the follower crosses in front of the leader while turning.",
    category: ["Dance"],
    danceStyles: [10, 11, 12],
    multimedia: [1, 3],
    seeAlso: [14, 15, 16, 21, 44],
  },
  {
    id: 14,
    name: "Clave",
    definition:
      "The rhythmic foundation of Salsa music, typically a 3-2 or 2-3 pattern that guides the dancers timing.",
    category: ["Music"],
    danceStyles: [9, 10, 11, 12],
    multimedia: [4, 5],
    seeAlso: [13, 17, 18, 45, 46],
  },
  {
    id: 15,
    name: "Montuno",
    definition:
      "A section in Salsa music characterized by call-and-response vocals and improvisation over a repeated chord progression.",
    category: ["Music"],
    danceStyles: [9, 10, 11, 12],
    multimedia: [4, 5],
    seeAlso: [14, 18, 45, 62],
  },
  {
    id: 16,
    name: "Rueda de Casino",
    definition:
      "A group dance format in Cuban Salsa where couples dance in a circle and execute synchronized moves called by a leader.",
    category: ["Dance"],
    danceStyles: [10],
    multimedia: [1, 3, 4],
    seeAlso: [13, 19, 20, 47],
  },
  {
    id: 17,
    name: "Son",
    definition:
      "A genre of music and dance from Cuba that is one of the fundamental roots of modern Salsa.",
    category: ["History"],
    danceStyles: [9, 10],
    multimedia: [4, 5],
    seeAlso: [14, 18, 47, 62, 63],
  },
  {
    id: 18,
    name: "Rumba",
    definition:
      "A family of rhythm and dance styles from Cuba, influential in the development of many Latin dances.",
    category: ["History"],
    danceStyles: [9, 10],
    multimedia: [4, 5],
    seeAlso: [14, 15, 17, 47, 48],
  },
  {
    id: 19,
    name: "Casino",
    definition:
      "The Cuban style of Salsa dancing, characterized by circular movements and Afro-Cuban influences.",
    category: ["Dance"],
    danceStyles: [10],
    multimedia: [1, 3, 4],
    seeAlso: [16, 17, 18, 47],
  },
  {
    id: 20,
    name: "Mambo",
    definition:
      "A ballroom dance style that influenced Salsa, characterized by sharp movements and breaks on the second beat.",
    category: ["History"],
    danceStyles: [9, 11, 12],
    multimedia: [3, 4],
    seeAlso: [13, 17, 21, 48, 62],
  },
  {
    id: 21,
    name: "Shine",
    definition:
      "Solo footwork patterns performed in Salsa, allowing dancers to showcase individual style and technique.",
    category: ["Dance"],
    danceStyles: [10, 11, 12],
    multimedia: [1, 3],
    seeAlso: [13, 20, 22, 49],
  },
  {
    id: 22,
    name: "Suelta",
    definition:
      "Spanish for 'loose' or 'free', referring to open position dancing in Salsa where partners are not in hold.",
    category: ["Dance"],
    danceStyles: [10, 11, 12],
    multimedia: [1, 3],
    seeAlso: [21, 13, 49],
  },
  {
    id: 23,
    name: "Fusion",
    definition:
      "A contemporary dance approach that blends elements from multiple dance styles to create new movement vocabulary.",
    category: ["Dance"],
    danceStyles: [13],
    multimedia: [1, 3, 4],
    seeAlso: [2, 7, 24, 50, 51],
  },
  {
    id: 24,
    name: "Flow",
    definition:
      "The seamless transition between movements, creating a sense of continuous motion and connection in dance.",
    category: ["Dance"],
    danceStyles: [13, 1, 5],
    multimedia: [1, 3],
    seeAlso: [2, 23, 25, 50],
  },
  {
    id: 25,
    name: "Grounding",
    definition:
      "The connection and energy transfer between the dancer and the floor, creating stability and power in movement.",
    category: ["Dance"],
    danceStyles: [1, 2, 3, 13],
    multimedia: [1, 2],
    seeAlso: [5, 6, 24, 51],
  },
  {
    id: 27,
    name: "Angola",
    definition:
      "The birthplace of Kizomba, where traditional Semba dance evolved into modern Kizomba in the 1980s.",
    category: ["Place"],
    danceStyles: [1, 2, 3],
    multimedia: [4, 5],
    seeAlso: [26, 28, 29, 38, 53],
  },
  {
    id: 30,
    name: "Douceur",
    definition:
      "French for 'softness', referring to a gentle style of Kizomba emphasizing smooth, flowing movements.",
    category: ["Dance"],
    danceStyles: [1, 2],
    multimedia: [1, 3],
    seeAlso: [5, 25, 29, 37],
  },
  {
    id: 31,
    name: "Jazz",
    definition:
      "The musical foundation of swing dance, characterized by improvisation, complex rhythms, and syncopation.",
    category: ["Music", "Dance"],
    danceStyles: [5, 6, 7, 8],
    multimedia: [4, 5],
    seeAlso: [32, 33, 34, 40, 41],
  },
  {
    id: 32,
    name: "Big Band",
    definition:
      "A large jazz ensemble that was popular during the Swing Era, providing the music for social dancing.",
    category: ["Music"],
    danceStyles: [5, 6, 7],
    multimedia: [4, 5],
    seeAlso: [31, 33, 12, 40, 59],
  },
  {
    id: 33,
    name: "Swing Era",
    definition:
      "The period from the mid-1930s to mid-1940s when swing music and dance were at their peak popularity.",
    category: ["History"],
    danceStyles: [5, 6, 7],
    multimedia: [4, 5],
    seeAlso: [31, 32, 34, 40, 41],
  },
  {
    id: 34,
    name: "Harlem",
    definition:
      "The New York neighborhood where Lindy Hop was born and developed in the 1920s and 1930s.",
    category: ["Place"],
    danceStyles: [6],
    multimedia: [4, 5],
    seeAlso: [33, 35, 12, 43, 59],
  },
  {
    id: 35,
    name: "African Diaspora",
    definition:
      "The global spread of African peoples and their cultural influences, including dance and music traditions.",
    category: ["Culture"],
    danceStyles: [1, 3, 6, 9],
    multimedia: [4, 5],
    seeAlso: [6, 27, 34, 53, 54],
  },

  // NEW DANCE STYLE UMBRELLA ENTRIES (36-50)
  {
    id: 36,
    name: "Kizomba",
    definition:
      "A family of African partner dances originating from Angola, encompassing traditional Semba and modern urban styles like Urbankiz and Tarraxinha.",
    category: ["Dance", "Umbrella", "Music"],
    danceStyles: [1],
    multimedia: [3, 4, 5],
    seeAlso: [26, 37, 38, 39, 1, 4, 5],
  },
  {
    id: 37,
    name: "Urbankiz",
    definition:
      "A modern evolution of Kizomba that incorporates urban music styles and more dynamic movements while maintaining close connection.",
    category: ["Dance", "Music"],
    danceStyles: [2],
    multimedia: [1, 2, 3],
    seeAlso: [36, 38, 23, 50, 4, 30],
  },
  {
    id: 38,
    name: "Semba ",
    definition:
      "The energetic predecessor to Kizomba, characterized by bouncy movements, hip isolations, and strong African rhythmic patterns.",
    category: ["Dance", "Music", "Umbrella"],
    danceStyles: [3],
    multimedia: [1, 3, 4],
    seeAlso: [36, 28, 27, 6, 53],
  },
  {
    id: 39,
    name: "Tarraxinha",
    definition:
      "An intimate style of Kizomba danced to slower music, emphasizing micro-movements and deep partner connection.",
    category: ["Dance", "Music"],
    danceStyles: [4],
    multimedia: [1, 2, 3],
    seeAlso: [36, 29, 7, 2],
  },
  {
    id: 40,
    name: "Swing",
    definition:
      "A family of jazz-based partner dances that originated in African American communities, emphasizing improvisation and rhythmic complexity.",
    category: ["Dance", "Music", "Umbrella"],
    danceStyles: [5],
    multimedia: [3, 4, 5],
    seeAlso: [41, 42, 43, 31, 33, 8, 9],
  },
  {
    id: 41,
    name: "Lindy Hop",
    definition:
      "The original swing dance from Harlem's Savoy Ballroom, combining elements of Charleston, Tap, and Jazz with 8-count and 6-count patterns.",
    category: ["Dance"],
    danceStyles: [6],
    multimedia: [1, 3, 4],
    seeAlso: [40, 42, 43, 10, 12, 34, 59],
  },
  {
    id: 42,
    name: "East Coast Swing",
    definition:
      "A simplified 6-count swing dance developed for ballroom competitions, featuring circular patterns and basic rock-step timing.",
    category: ["Dance"],
    danceStyles: [7],
    multimedia: [1, 2, 3],
    seeAlso: [40, 41, 43, 11, 3],
  },
  {
    id: 43,
    name: "West Coast Swing",
    definition:
      "A slotted swing dance that evolved in California, characterized by elastic connection and adaptability to contemporary music.",
    category: ["Dance"],
    danceStyles: [8],
    multimedia: [1, 3, 4],
    seeAlso: [40, 41, 42, 10, 60],
  },
  {
    id: 44,
    name: "Salsa",
    definition:
      "A vibrant Latin dance family that combines Cuban Son, Puerto Rican, and New York influences with quick footwork and spins.",
    category: ["Dance", "Music", "Umbrella"],
    danceStyles: [9],
    multimedia: [3, 4, 5],
    seeAlso: [45, 46, 47, 13, 14, 17, 20],
  },
  {
    id: 45,
    name: "Cuban Salsa (Casino)",
    definition:
      "The original circular style of Salsa from Cuba, featuring Afro-Cuban body movements and rueda formations.",
    category: ["Dance", "Music"],
    danceStyles: [10],
    multimedia: [1, 3, 4],
    seeAlso: [44, 16, 19, 47, 17, 18],
  },
  {
    id: 46,
    name: "LA Style Salsa",
    definition:
      "A linear Salsa style from Los Angeles emphasizing performance, dips, and smooth Hollywood-influenced choreography.",
    category: ["Dance"],
    danceStyles: [11],
    multimedia: [1, 3],
    seeAlso: [44, 47, 13, 20, 49],
  },
  {
    id: 47,
    name: "NY Style Salsa",
    definition:
      "A linear Salsa style from New York featuring sharp movements, breaking on beat 2, and strong mambo influences.",
    category: ["Dance"],
    danceStyles: [12],
    multimedia: [1, 3, 4],
    seeAlso: [44, 45, 46, 20, 48, 62],
  },
  {
    id: 48,
    name: "Brazilian Zouk",
    definition:
      "A sensual partner dance from Brazil that evolved from Lambada, characterized by flowing movements and head isolations.",
    category: ["Dance"],
    danceStyles: [14],
    multimedia: [1, 3, 4],
    seeAlso: [49, 51, 54, 23, 7],
  },
  {
    id: 49,
    name: "Caribbean Zouk",
    definition:
      "The original Zouk from the French Caribbean islands, featuring faster tempo and more separated partner work.",
    category: ["Dance", "Music"],
    danceStyles: [15],
    multimedia: [1, 3, 4],
    seeAlso: [48, 54, 21, 22],
  },
  {
    id: 50,
    name: "Fusion Dancing",
    definition:
      "The art of blending multiple dance styles within a single song or dance, creating unique movement combinations and personal expression.",
    category: ["Dance"],
    danceStyles: [13],
    multimedia: [1, 3, 4],
    seeAlso: [23, 24, 37, 51, 43],
  },

  // HISTORY ENTRIES (51-65)
  {
    id: 51,
    name: "Lambada Origins",
    definition:
      "The controversial Brazilian dance of the 1980s that combined Forró, Merengue, and Caribbean influences, serving as the foundation for modern Brazilian Zouk.",
    category: ["History"],
    danceStyles: [14],
    multimedia: [4, 5],
    seeAlso: [48, 54, 55, 25],
  },
  {
    id: 52,
    name: "Savoy Ballroom",
    definition:
      "The legendary Harlem dance hall (1926-1958) where Lindy Hop flourished and integrated audiences danced together during segregation.",
    category: ["Place"],
    danceStyles: [6],
    multimedia: [4, 5],
    seeAlso: [34, 41, 33, 59, 35],
  },
  {
    id: 53,
    name: "Portuguese Colonial Influence",
    definition:
      "The historical impact of Portuguese colonization on Angolan music and dance, creating the cultural fusion that birthed Semba and later Kizomba.",
    category: ["History"],
    danceStyles: [1, 3],
    multimedia: [4, 5],
    seeAlso: [27, 28, 38, 35, 6],
  },
  {
    id: 54,
    name: "French Caribbean Culture",
    definition:
      "The cultural context of Martinique and Guadeloupe that produced Zouk music and dance in the 1980s.",
    category: ["Culture"],
    danceStyles: [15],
    multimedia: [4, 5],
    seeAlso: [49, 51, 35, 55],
  },
  {
    id: 55,
    name: "Latin Music Revolution",
    definition:
      "The 1970s-80s explosion of Latin music that globalized Salsa and created the foundation for modern Latin partner dancing.",
    category: ["History"],
    danceStyles: [9, 10, 11, 12],
    multimedia: [4, 5],
    seeAlso: [44, 17, 48, 62, 51],
  },
  {
    id: 56,
    name: "Ballroom Standardization",
    definition:
      "The 20th century process of codifying partner dances for competition, which simplified traditional forms like East Coast Swing.",
    category: ["History"],
    danceStyles: [7],
    multimedia: [3, 4],
    seeAlso: [42, 8, 57, 58],
  },
  {
    id: 57,
    name: "Social Dance Evolution",
    definition:
      "The transformation of folk and traditional dances into partner social dances through cultural exchange and urbanization.",
    category: ["History"],
    danceStyles: [1, 5, 9],
    multimedia: [3, 4, 5],
    seeAlso: [35, 52, 55, 56],
  },
  {
    id: 58,
    name: "Palladium Era",
    definition:
      "The golden age of Mambo and early Salsa at New York's Palladium Ballroom (1948-1966), where Latin and jazz cultures merged.",
    category: ["History"],
    danceStyles: [9, 12],
    multimedia: [4, 5],
    seeAlso: [20, 47, 55, 62],
  },
  {
    id: 59,
    name: "Frankie Manning",
    definition:
      "Legendary Lindy Hop dancer and choreographer (1914-2009) who preserved and revived swing dance worldwide.",
    category: ["Person"],
    danceStyles: [6],
    multimedia: [3, 4, 5],
    seeAlso: [41, 52, 12, 32, 34],
  },
  {
    id: 60,
    name: "Dean Collins",
    definition:
      "Influential swing dancer (1917-1984) who brought Lindy Hop to Hollywood and developed the smooth West Coast Swing style.",
    category: ["Person"],
    danceStyles: [8],
    multimedia: [3, 4],
    seeAlso: [43, 59, 61],
  },
  {
    id: 61,
    name: "Celia Cruz",
    definition:
      "The 'Queen of Salsa' (1925-2003), whose powerful voice and stage presence helped popularize Salsa music and dance worldwide.",
    category: ["Person"],
    danceStyles: [9, 10, 11, 12],
    multimedia: [4, 5],
    seeAlso: [44, 55, 62, 63],
  },
  {
    id: 62,
    name: "Tito Puente",
    definition:
      "The 'King of Latin Music' (1923-2000), legendary timbales player and bandleader who shaped the sound of Mambo and Salsa.",
    category: ["Person"],
    danceStyles: [9, 12],
    multimedia: [4, 5],
    seeAlso: [20, 47, 58, 61, 15],
  },
  {
    id: 63,
    name: "Benny Moré",
    definition:
      "Cuban singer and bandleader (1919-1963) known as 'El Bárbaro del Ritmo', whose music became foundational to Cuban Salsa.",
    category: ["Person"],
    danceStyles: [10],
    multimedia: [4, 5],
    seeAlso: [17, 45, 61, 64],
  },
  {
    id: 64,
    name: "Eduardo Paim",
    definition:
      "Angolan musician credited as one of the pioneers of modern Kizomba music in the 1980s, blending traditional Semba with zouk influences.",
    category: ["Person"],
    danceStyles: [1],
    multimedia: [4, 5],
    seeAlso: [26, 36, 53, 65],
  },
  {
    id: 65,
    name: "Kassav'",
    definition:
      "Guadeloupean band formed in 1979 that created and popularized Zouk music, influencing dance styles across the Caribbean and beyond.",
    category: ["Person"],
    danceStyles: [15],
    multimedia: [4, 5],
    seeAlso: [49, 54, 51, 64],
  },
];
